// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef, useState } from 'react';
import { calculationResultApi } from '../../../../api/calculationResultApi';
import type {
  CalculatePreviewResponse,
  MaxFundingScenarioInputs,
} from '../../../../types/calculationResult';

const DEFAULT_DEBOUNCE_MS = 300;

function inputsLookValid(inputs: MaxFundingScenarioInputs): boolean {
  return (
    Number.isFinite(inputs.retirementAge) &&
    inputs.retirementAge >= 60 &&
    inputs.retirementAge <= 75 &&
    Number.isFinite(inputs.currentGrossPayeEarnings) &&
    inputs.currentGrossPayeEarnings > 0 &&
    Number.isFinite(inputs.yearsWithCurrentEmployerToDate) &&
    inputs.yearsWithCurrentEmployerToDate >= 0 &&
    inputs.yearsWithCurrentEmployerToDate <= 40 &&
    !!inputs.dateOfBirth &&
    !!inputs.gender &&
    !!inputs.maritalStatus
  );
}

export interface UseMaxFundingComputeResult {
  outputs: CalculatePreviewResponse['outputs'] | null;
  warnings: string[];
  isLoading: boolean;
  error: string | null;
}

export function useMaxFundingCompute(
  inputs: MaxFundingScenarioInputs,
  debounceMs: number = DEFAULT_DEBOUNCE_MS,
): UseMaxFundingComputeResult {
  const [outputs, setOutputs] = useState<CalculatePreviewResponse['outputs'] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!inputsLookValid(inputs)) {
      setOutputs(null);
      setWarnings([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const timer = globalThis.setTimeout(async () => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        setIsLoading(true);
        setError(null);
        const response = await calculationResultApi.calculate(
          'MAXFUNDING',
          inputs,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setOutputs(response.outputs);
          setWarnings(response.warnings ?? []);
        }
      } catch (err) {
        if ((err as { name?: string })?.name === 'CanceledError' ||
            (err as { code?: string })?.code === 'ERR_CANCELED') {
          return;
        }
        const apiError = err as { response?: { data?: { message?: string } }; message?: string };
        setError(apiError.response?.data?.message || apiError.message || 'Calculation failed');
        setOutputs(null);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      globalThis.clearTimeout(timer);
      controllerRef.current?.abort();
    };
  }, [inputs, debounceMs]);

  return { outputs, warnings, isLoading, error };
}
